import * as types from './actionTypes';
import * as timelineTypes from '../timeline/actionTypes';
import * as agentRegistryTypes from '../agentRegistry/actionTypes';
import _ from 'lodash';
import moment from 'moment';
import {
  buildOldSensorLabelMap,
  resolveAgentLabel
} from '../../utils/agentUtil';

// Labels every Agents feature once, here, so the map, the popup and the filter
// panel all read the same string from properties.DisplayLabel instead of each
// re-deriving it from Name / AgentName / EntityName.
//
// The whole feature set is needed in one pass because "Old sensor agent N" is
// numbered across the set.
const applyAgentDisplayLabels = (features, registry) => {
  const list = Array.isArray(features) ? features : [];
  const oldSensorLabelMap = buildOldSensorLabelMap(
    list.reduce((values, feature) => {
      const props = (feature && feature.properties) || {};
      return values.concat([
        props.EntityId,
        props.Name,
        props.AgentName,
        props.EntityName
      ]);
    }, [])
  );
  list.forEach(feature => {
    if (!feature || !feature.properties) return;
    feature.properties.DisplayLabel = resolveAgentLabel(feature, {
      registry,
      oldSensorLabelMap
    });
  });
  return list;
};

const normalizeRegistryKeys = entries => {
  const registry = {};
  Object.keys(entries || {}).forEach(id => {
    const key = (id || '')
      .toString()
      .trim()
      .toLowerCase();
    if (key && entries[id]) registry[key] = entries[id];
  });
  return registry;
};

const clearEntityTypeSelectionFlags = entityType => {
  let value = String(entityType || '');
  let changed = true;
  while (changed) {
    changed = false;
    if (value.endsWith('_clicked')) {
      value = value.substring(0, value.length - 8);
      changed = true;
    }
    if (value.endsWith('_selected')) {
      value = value.substring(0, value.length - 9);
      changed = true;
    }
  }
  return value;
};

const reducer = (
  state = {
    layers: [],
    trackLayer: {
      type: 'FeatureCollection',
      features: []
    },
    alertFeatures: [],
    selectedLayers: ['Observations', 'Tracks', 'Agents'],
    camtrapFocusIds: null,
    camtrapResults: [],
    heatmaps: {},
    fullTrackTraces: {}
  },
  action
) => {
  let clonedLayers = [];
  let clonedAlertFeatures = [];

  switch (action.type) {
    case types.SET_PROJECT_LAYERS_SUCCESS: {
      let mapLayers = [];
      Object.keys(action.payload).forEach(key => {
        if (key !== 'track' && key !== 'test') {
          action.payload[key].layers.forEach(layer => {
            layer.projectId = key;
            mapLayers.push(layer);
          });
        }
      });
      return {
        ...state,
        layers: mapLayers
      };
    }
    case types.SET_ALERT_FEATURES_SUCCESS: {
      clonedAlertFeatures = action.payload.data.features.map(feature => {
        if (feature.properties.EntityId === action.payload.selectedItem) {
          feature.properties.EntityType =
            feature.properties.EntityType + '_clicked';
        }

        return feature;
      });

      return {
        ...state,
        alertFeatures: clonedAlertFeatures
      };
    }
    case types.SET_LAYER_FEATURES_SUCCESS: {
      clonedLayers = state.layers.map(layer => {
        if (
          layer.projectId === action.payload.projectId &&
          layer.id === action.payload.layerId
        ) {
          let newData = action.payload.data;
          if (newData && newData.features) {
            const now = moment();
            newData.features.map(item => {
              if (
                layer.name === 'Tracks' &&
                item &&
                item.properties &&
                item.properties.EntityType
              ) {
                // Defensive cleanup: track features can carry stale UI suffixes
                // when payload objects are reused across updates.
                item.properties.EntityType = clearEntityTypeSelectionFlags(
                  item.properties.EntityType
                );
              }

              if (item.properties.EntityId === action.payload.selectedItem) {
                item.properties.EntityType =
                  item.properties.EntityType + '_clicked';
              }

              if (layer.name === 'Agents') {
                const lastSeen = moment(item.properties.LastSeenDateTime);
                if (now.diff(lastSeen, 'hours') < 1) {
                  item.properties.age = 'lessThanHour';
                } else if (now.isSameOrBefore(lastSeen, 'day')) {
                  item.properties.age = 'lessThanDay';
                } else if (now.diff(lastSeen, 'days') <= 7) {
                  item.properties.age = 'moreThanDay';
                } else {
                  item.properties.age = 'moreThanWeek';
                }
              }

              if (
                layer.name === 'Observations' &&
                action.payload.selectedTrackObservations !== null
              ) {
                if (
                  action.payload.selectedTrackObservations.includes(
                    item.properties.EntityId
                  )
                ) {
                  item.properties.EntityType =
                    item.properties.EntityType + '_selected';
                }
              }
              return item;
            });
            if (layer.name === 'Agents') {
              applyAgentDisplayLabels(
                newData.features,
                action.payload.agentRegistry
              );
            }
            return { ...layer, ...newData };
          }
        }
        return layer;
      });

      return {
        ...state,
        layers: clonedLayers
      };
    }
    // Registrations resolve asynchronously, so relabel the Agents features that
    // were already in the store when the names arrived.
    case agentRegistryTypes.AGENT_REGISTRY_MERGE: {
      const registry = normalizeRegistryKeys(
        action.payload && action.payload.entries
      );
      if (!Object.keys(registry).length) return state;

      let changed = false;
      const relabelledLayers = state.layers.map(layer => {
        if (layer.name !== 'Agents' || !Array.isArray(layer.features)) {
          return layer;
        }
        changed = true;
        return {
          ...layer,
          features: applyAgentDisplayLabels(
            layer.features.map(feature => ({
              ...feature,
              properties: { ...(feature.properties || {}) }
            })),
            registry
          )
        };
      });

      if (!changed) return state;
      return {
        ...state,
        layers: relabelledLayers
      };
    }
    case types.SET_LAYER_HEATMAP_SUCCESS: {
      return {
        ...state,
        heatmaps: {
          ...state.heatmaps,
          [action.payload.layerName]: action.payload.data
        }
      };
    }
    case types.SET_FULL_TRACK_TRACE: {
      return {
        ...state,
        fullTrackTraces: {
          ...state.fullTrackTraces,
          [action.payload.trackId]: action.payload.data
        }
      };
    }
    case types.CLEAR_COMMON_LAYER_FEATURES: {
      clonedLayers = state.layers.map(layer => {
        if (
          layer.name === 'Observations' ||
          layer.name === 'Tracks' ||
          layer.name === 'Agents'
        ) {
          return {
            ...layer,
            type: 'FeatureCollection',
            features: []
          };
        }
        return layer;
      });
      return {
        ...state,
        layers: clonedLayers
      };
    }
    case timelineTypes.SET_SELECTED_ITEM:
      if (action.payload.observationId) {
        clonedAlertFeatures = state.alertFeatures.map(feature => {
          if (
            feature.properties.EntityId === action.payload.observationId &&
            !feature.properties.EntityType.includes('_clicked')
          ) {
            if (feature.properties.EntityType.includes('_selected')) {
              feature.properties.EntityType =
                feature.properties.EntityType.substring(
                  0,
                  feature.properties.EntityType.length - 9
                ) + '_clicked';
            } else {
              feature.properties.EntityType =
                feature.properties.EntityType + '_clicked';
            }
          } else if (feature.properties.EntityType.includes('_clicked')) {
            feature.properties.EntityType = feature.properties.EntityType.substring(
              0,
              feature.properties.EntityType.length - 8
            );
          } else if (feature.properties.EntityType.includes('_selected')) {
            feature.properties.EntityType = feature.properties.EntityType.substring(
              0,
              feature.properties.EntityType.length - 9
            );
          }
          return feature;
        });
        clonedLayers = state.layers.map(layer => {
          if (layer.name === 'Observations' && layer.features) {
            let clonedFeatures = layer.features.map(feature => {
              if (
                feature.properties.EntityId === action.payload.observationId &&
                !feature.properties.EntityType.includes('_clicked')
              ) {
                if (feature.properties.EntityType.includes('_selected')) {
                  feature.properties.EntityType =
                    feature.properties.EntityType.substring(
                      0,
                      feature.properties.EntityType.length - 9
                    ) + '_clicked';
                } else {
                  feature.properties.EntityType =
                    feature.properties.EntityType + '_clicked';
                }
              } else if (feature.properties.EntityType.includes('_clicked')) {
                feature.properties.EntityType = feature.properties.EntityType.substring(
                  0,
                  feature.properties.EntityType.length - 8
                );
              } else if (feature.properties.EntityType.includes('_selected')) {
                feature.properties.EntityType = feature.properties.EntityType.substring(
                  0,
                  feature.properties.EntityType.length - 9
                );
              }
              return feature;
            });
            layer.features = clonedFeatures;
          }
          if (layer.name === 'Tracks' && layer.features) {
            let clonedFeatures = layer.features.map(feature => {
              if (feature.properties.EntityType.includes('_clicked')) {
                feature.properties.EntityType = feature.properties.EntityType.substring(
                  0,
                  feature.properties.EntityType.length - 8
                );
              }
              return feature;
            });
            layer.features = clonedFeatures;
          }
          if (layer.name === 'Agents' && layer.features) {
            let clonedFeatures = layer.features.map(feature => {
              if (feature.properties.age.includes('_clicked')) {
                feature.properties.age = feature.properties.age.substring(
                  0,
                  feature.properties.age.length - 8
                );
              }
              return feature;
            });
            layer.features = clonedFeatures;
          }
          return layer;
        });
      } else if (action.payload.featureId) {
        clonedLayers = state.layers.map(layer => {
          if (layer.name === 'Tracks' && layer.features) {
            let clonedFeatures = layer.features.map(feature => {
              if (
                feature.properties.EntityId === action.payload.featureId &&
                !feature.properties.EntityType.includes('_clicked')
              ) {
                feature.properties.EntityType =
                  feature.properties.EntityType + '_clicked';
              } else if (feature.properties.EntityType.includes('_clicked')) {
                feature.properties.EntityType = feature.properties.EntityType.substring(
                  0,
                  feature.properties.EntityType.length - 8
                );
              }
              return feature;
            });
            layer.features = clonedFeatures;
          }
          if (layer.name === 'Observations' && layer.features) {
            let clonedFeatures = layer.features.map(feature => {
              if (feature.properties.EntityType.includes('_clicked')) {
                feature.properties.EntityType = feature.properties.EntityType.substring(
                  0,
                  feature.properties.EntityType.length - 8
                );
              } else if (feature.properties.EntityType.includes('_selected')) {
                feature.properties.EntityType = feature.properties.EntityType.substring(
                  0,
                  feature.properties.EntityType.length - 9
                );
              }
              return feature;
            });
            layer.features = clonedFeatures;
          }
          if (layer.name === 'Agents' && layer.features) {
            let clonedFeatures = layer.features.map(feature => {
              if (feature.properties.age.includes('_clicked')) {
                feature.properties.age = feature.properties.age.substring(
                  0,
                  feature.properties.age.length - 8
                );
              }
              return feature;
            });
            layer.features = clonedFeatures;
          }
          return layer;
        });
      } else {
        clonedLayers = state.layers.map(layer => {
          if (layer.name === 'Agents' && layer.features) {
            let clonedFeatures = layer.features.map(feature => {
              if (
                feature.properties.EntityId ===
                  action.payload.properties.EntityId &&
                !feature.properties.age.includes('_clicked')
              ) {
                feature.properties.age = feature.properties.age + '_clicked';
              } else if (feature.properties.age.includes('_clicked')) {
                feature.properties.age = feature.properties.age.substring(
                  0,
                  feature.properties.age.length - 8
                );
              }
              return feature;
            });
            layer.features = clonedFeatures;
          }
          if (layer.name === 'Observations' && layer.features) {
            let clonedFeatures = layer.features.map(feature => {
              if (feature.properties.EntityType.includes('_clicked')) {
                feature.properties.EntityType = feature.properties.EntityType.substring(
                  0,
                  feature.properties.EntityType.length - 8
                );
              } else if (feature.properties.EntityType.includes('_selected')) {
                feature.properties.EntityType = feature.properties.EntityType.substring(
                  0,
                  feature.properties.EntityType.length - 9
                );
              }
              return feature;
            });
            layer.features = clonedFeatures;
          }
          if (layer.name === 'Tracks' && layer.features) {
            let clonedFeatures = layer.features.map(feature => {
              if (feature.properties.EntityType.includes('_clicked')) {
                feature.properties.EntityType = feature.properties.EntityType.substring(
                  0,
                  feature.properties.EntityType.length - 8
                );
              }
              return feature;
            });
            layer.features = clonedFeatures;
          }
          return layer;
        });
      }
      return {
        ...state,
        layers: clonedLayers,
        alertFeatures: clonedAlertFeatures
      };
    case timelineTypes.CLEAR_SELECTED_ITEM:
      clonedLayers = state.layers.map(layer => {
        if (layer.name === 'Observations' && layer.features) {
          clonedAlertFeatures = (state.alertFeatures || []).map(feature => {
            if (feature.properties && feature.properties.EntityType) {
              feature.properties.EntityType = clearEntityTypeSelectionFlags(
                feature.properties.EntityType
              );
            }
            return feature;
          });

          let clonedFeatures = layer.features.map(feature => {
            if (feature.properties && feature.properties.EntityType) {
              feature.properties.EntityType = clearEntityTypeSelectionFlags(
                feature.properties.EntityType
              );
            }
            return feature;
          });
          layer.features = clonedFeatures;
        } else if (layer.name === 'Tracks' && layer.features) {
          let clonedFeatures = layer.features.map(feature => {
            if (feature.properties && feature.properties.EntityType) {
              feature.properties.EntityType = clearEntityTypeSelectionFlags(
                feature.properties.EntityType
              );
            }
            return feature;
          });
          layer.features = clonedFeatures;
        } else if (layer.name === 'Agents' && layer.features) {
          let clonedFeatures = layer.features.map(feature => {
            if (
              feature.properties &&
              feature.properties.age.includes('_clicked')
            ) {
              feature.properties.age = feature.properties.age.substring(
                0,
                feature.properties.age.length - 8
              );
            }
            return feature;
          });
          layer.features = clonedFeatures;
        }
        return layer;
      });
      return {
        ...state,
        layers: clonedLayers,
        alertFeatures: clonedAlertFeatures,
        trackLayer: {
          type: 'FeatureCollection',
          features: []
        }
      };
    case types.SET_ENTIRE_TRACK:
      return {
        ...state,
        trackLayer: action.payload
      };
    case types.CLEAR_ENTIRE_TRACK:
      return {
        ...state,
        trackLayer: {
          type: 'FeatureCollection',
          features: []
        }
      };
    case types.SET_TRACK_OBSERVATIONS: {
      const observations = Array.isArray(action.payload) ? action.payload : [];
      const arrayOfIds = observations.map(
        item => item.Observation.observationId
      );
      clonedLayers = state.layers.map(layer => {
        if (layer.name === 'Observations' && layer.features) {
          let clonedFeatures = layer.features.map(feature => {
            if (arrayOfIds.includes(feature.properties.EntityId)) {
              feature.properties.EntityType =
                feature.properties.EntityType + '_selected';
            }
            return feature;
          });
          layer.features = clonedFeatures;
        }
        return layer;
      });
      return {
        ...state,
        layers: clonedLayers
      };
    }
    case types.SET_SELECTED_LAYERS: {
      return {
        ...state,
        selectedLayers: action.payload
      };
    }
    case types.SET_CAMTRAP_FOCUS:
      return { ...state, camtrapFocusIds: action.payload };
    case types.CLEAR_CAMTRAP_FOCUS:
      return { ...state, camtrapFocusIds: null, camtrapResults: [] };
    case types.SET_CAMTRAP_RESULTS:
      return { ...state, camtrapResults: action.payload };
    case types.SET_CAMTRAP_OBSERVATION_MEDIA:
      return {
        ...state,
        camtrapResults: state.camtrapResults.map(item => {
          if (item.id === action.payload.observationId) {
            item.extracted.content.map(content => {
              if (content.Observation) {
                content.Observation.media = action.payload.values;
              }
              return content;
            });
          }
          return item;
        })
      };
    default:
      return state;
  }
};

const data = state =>
  _.orderBy(state.layers, [layer => layer.name.toLowerCase()], ['asc']);
const trackLayer = state => state.trackLayer;
const alertFeatures = state => state.alertFeatures;
const heatmaps = state => state.heatmaps || {};
const fullTrackTraces = state => state.fullTrackTraces || {};
const layerDataSelectors = {
  data,
  trackLayer,
  alertFeatures,
  heatmaps,
  fullTrackTraces
};

export default reducer;
export { layerDataSelectors };
